'use client';

import { Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface ConsultingTeaserSectionProps {
  isLoading?: boolean;
}

export function ConsultingTeaserSection({
  isLoading = false,
}: ConsultingTeaserSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[800px]">
          <Skeleton className="mx-auto h-40 w-full rounded-2xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div
        className="mx-auto max-w-[800px] rounded-2xl p-8 text-center md:p-12"
        style={{
          background: isDark
            ? `${theme.colors.accent.primary.dark}1a`
            : `${theme.colors.accent.primary.light}14`,
          border: `1px solid ${
            isDark
              ? `${theme.colors.accent.primary.dark}4d`
              : `${theme.colors.accent.primary.light}33`
          }`,
        }}
      >
        <h2
          className="text-gradient-hero mb-3 text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          AI made your team faster. <br />
          I'll help make them better.
        </h2>
        <p
          className="text-foreground/70 mb-6 text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          When agent-written code outpaces review, I find what gets missed,
          build the guardrails that catch it, and coach your team to keep the
          bar high.
        </p>
        <Link
          to="/consulting"
          className="text-foreground/80 inline-flex items-center gap-2 font-semibold underline transition-opacity hover:opacity-70"
          style={{
            fontFamily: theme.fonts.body,
            textDecorationColor: theme.colors.semantic.info,
          }}
        >
          See consulting
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
